import { getGlobalConfig, macroCondition } from "@embroider/macros";

//#region package.json
var name = "@warp-drive/core";
var version = "5.9.1";

//#endregion
//#region src/types/-private.ts
const GlobalRef = globalThis;
const UniversalCache = GlobalRef.__warpDrive_universalCache = GlobalRef.__warpDrive_universalCache ?? {};
GlobalRef[name] = GlobalRef[name] ?? { __version: version };
const GlobalSink = GlobalRef[name];
if (macroCondition(getGlobalConfig().WarpDrive.env.DEBUG)) {
	if (GlobalSink.__version !== version) throw new Error("Multiple versions of WarpDrive detected, the application will malfunction.");
}
const ModuleScopedCaches = GlobalSink.__warpDrive_ModuleScopedCaches ?? {};
if (macroCondition(getGlobalConfig().WarpDrive.env.TESTING)) {
	if (!GlobalSink.__warpDrive_ModuleScopedCaches) GlobalSink.__warpDrive_ModuleScopedCaches = ModuleScopedCaches;
	else console.warn(`
Multiple copies of WarpDrive have been detected. This may be due to a bug in ember-auto-import
  in which test assets get their own copy of some v2-addons. This can cause the application to
  malfunction as each copy will maintain its own separate state.`);
} else {
	if (GlobalSink.__warpDrive_hasOtherCopy) throw new Error("Multiple copies of WarpDrive detected, the application will malfunction.");
	GlobalSink.__warpDrive_hasOtherCopy = true;
}
/**
* Coordinates creation of a singleton `value` (e.g. a symbol, WeakMap, or
* error class) for `key` so that duplicate copies of this package loaded
* into the same process observe the same instance instead of each creating
* their own.
*
* In `TESTING` builds this stores/reads `value` from a cache namespaced to
* this package's name on `globalThis` (see the file-level comments above for
* why this is necessary). Outside of `TESTING`, only one copy of the package
* is expected to exist, so this is a no-op that always returns `value`
* unchanged.
*
* The return type lies about symbols being unique to work around the d.ts
* rollup issue described in `reactive/-private/symbols.ts`.
*
* @private
*/
function getOrSetGlobal(key, value) {
	if (macroCondition(getGlobalConfig().WarpDrive.env.TESTING)) {
		const existing = ModuleScopedCaches[key];
		if (existing === void 0) return ModuleScopedCaches[key] = value;
		else return existing;
	} else return value;
}
/**
* Reads back the current value stored for `key` by {@link setTransient}, or
* `null` if nothing has been stored yet.
*
* Unlike {@link getOrSetGlobal}, this always reads through the same
* package-namespaced cache regardless of the `TESTING` flag, since this is
* used for genuinely mutable state (not just singleton values) that must
* stay in sync across any duplicate copies of this package.
*
* @private
*/
function peekTransient(key) {
	const globalKey = `(transient) ${key}`;
	return ModuleScopedCaches[globalKey] ?? null;
}
/**
* Stores `value` for `key`, overwriting whatever was previously stored, so
* that it can later be read back via {@link peekTransient}.
*
* See {@link peekTransient} for details on when/why this cache is shared.
*
* @private
*/
function setTransient(key, value) {
	const globalKey = `(transient) ${key}`;
	return ModuleScopedCaches[globalKey] = value;
}
/**
* Like {@link getOrSetGlobal}, but reads/writes a cache that is shared across
* *all* copies of WarpDrive on `globalThis`, regardless of package name.
*
* This is used for keys (e.g. request/promise caches) that must be
* coordinated even across "mirror" packages published under different
* names, rather than only across duplicate copies of the same package name.
*
* @private
*/
function getOrSetUniversal(key, value) {
	if (macroCondition(getGlobalConfig().WarpDrive.env.TESTING)) {
		const existing = UniversalCache[key];
		if (existing === void 0) return UniversalCache[key] = value;
		else return existing;
	} else return value;
}
/**
* Like {@link peekTransient}, but reads from the package-name-independent
* cache used by {@link getOrSetUniversal}. Always reads through this shared
* cache regardless of the `TESTING` flag.
*
* @private
*/
function peekUniversalTransient(key) {
	const globalKey = `(transient) ${key}`;
	return UniversalCache[globalKey] ?? null;
}
/**
* Like {@link setTransient}, but writes to the package-name-independent
* cache used by {@link getOrSetUniversal}, so it can later be read via
* {@link peekUniversalTransient}.
*
* @private
*/
function setUniversalTransient(key, value) {
	const globalKey = `(transient) ${key}`;
	return UniversalCache[globalKey] = value;
}

//#endregion
export { setTransient as a, peekUniversalTransient as i, getOrSetUniversal as n, setUniversalTransient as o, peekTransient as r, getOrSetGlobal as t };
//# sourceMappingURL=-private-sUo5R_vX.js.map